import React, { useMemo } from "react";
import { Box, Paper, Typography, Skeleton } from "@mui/material";
import { Flag24Regular } from "@fluentui/react-icons";
import { THEME, useOverviewData } from "./OverviewContext";
import FluentDonutChart from "../../../ChartComponents/FluentDountChart";
import { CUSTOMER_VIEW_STYLES } from "../../CustomerView/CustomerView.styles";

// Priority color configuration
const PRIORITY_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
    P0: { color: "#D13438", bgColor: "#FDE7E9", label: "Critical" },
    P1: { color: "#E74C3C", bgColor: "#FDEDEC", label: "High" },
    P2: { color: "#F39C12", bgColor: "#FEF5E7", label: "Medium" },
    P3: { color: "#5A5A72", bgColor: "#F5F5F5", label: "Low" }
};

const PriorityBreakdown: React.FC = () => {
    const { data, isLoading } = useOverviewData();

    const priorityData = data?.priorityBreakdown ?? [];
    const totalCount = priorityData.reduce((sum, p) => sum + p.count, 0);

    // Transform to donut chart format
    const chartData = useMemo(() => {
        return priorityData.map((item) => {
            const config = PRIORITY_CONFIG[item.priority] || {
                color: THEME.textSecondary,
                bgColor: "#F5F5F5",
                label: item.priority
            };
            return {
                legend: `${item.priority} - ${config.label}`,
                data: item.count,
                color: config.color
            };
        });
    }, [priorityData]);

    if (isLoading) {
        return <Skeleton variant="rectangular" height={280} sx={{ borderRadius: "12px" }} />;
    }

    if (priorityData.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: "12px",
                    border: `1px solid ${THEME.border}`,
                    backgroundColor: THEME.surface,
                    height: 280
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Flag24Regular style={{ color: THEME.primary }} />
                    <Typography sx={{ fontSize: "16px", fontWeight: 700, color: THEME.textPrimary }}>
                        Customer Request Breakdown by Priority
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: "13px", color: THEME.textSecondary, textAlign: "center", py: 3 }}>
                    No priority data available.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: "12px",
                border: `1px solid ${THEME.border}`,
                backgroundColor: THEME.surface,
                height: 280,
                display: "flex",
                flexDirection: "column"
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexShrink: 0 }}>
                <Flag24Regular style={{ color: THEME.primary }} />
                <Typography sx={{ fontSize: "16px", fontWeight: 700, color: THEME.textPrimary }}>
                    Customer Request Breakdown by Priority
                </Typography>
            </Box>

            {/* Donut Chart */}
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                <FluentDonutChart
                    data={{
                        chartTitle: "Priority Breakdown",
                        chartData: chartData
                    }}
                    height={200}
                    width={320}
                    valueInsideDonut={totalCount}
                    styles={CUSTOMER_VIEW_STYLES.COMMON_STYLES.CSS_STYLES.donutChart}
                />
            </Box>
        </Paper>
    );
};

export default PriorityBreakdown;
